// Optional protocol, domain + subdomains, top-level domain, optional path + query
const linkRegex = /^(https?:\/\/)?([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(\/\S*)?$/;

/**
 * Sanitizes a URL by removing invisible characters.
 */
export function sanitizeUrl(url: string): string {
  return url
    .replaceAll(/%E2%80%8B/g, "") // URL-encoded zero-width space
    .replaceAll(/%EF%BB%BF/g, ""); // URL-encoded byte order mark
}

export function isValidLink(link: string): boolean {
  return linkRegex.test(link.toLowerCase());
}

export function ensureProtocolPresent(link: string): string {
  if (!/^https?:\/\//i.test(link)) {
    return `https://${link}`;
  }
  return link;
}

/**
 * Returns true if `url` is safe to render as `<a href>` or `<img src>`
 * inside an LLM-generated surface.
 *
 * "Safe" means: an http/https/mailto scheme, OR a same-origin
 * absolute/relative path. Everything else — `javascript:`, `data:`,
 * `vbscript:`, `file:`, etc. — is rejected. Same threat model as
 * `react-markdown`'s built-in `defaultUrlTransform`, exposed here so
 * any surface that renders model-emitted URLs outside of markdown
 * (file attachment chips, custom renderers, etc.) can use it too.
 */
export function isSafeHttpUrl(url: string | undefined | null): url is string {
  if (typeof url !== "string" || url.length === 0) {
    return false;
  }
  const trimmed = url.trimStart();
  // Same-origin relative URLs are always safe.
  if (
    trimmed.startsWith("/") ||
    trimmed.startsWith("#") ||
    trimmed.startsWith("?") ||
    trimmed.startsWith("./") ||
    trimmed.startsWith("../")
  ) {
    return true;
  }
  // Allowlisted absolute schemes (case-insensitive per RFC 3986).
  return /^(https?|mailto):/i.test(trimmed);
}
